import { Fragment, useState, useContext, useEffect } from "react";
import DataContext from "../../context/data-context";
import PaymentDetail from "./PaymentCard";
import SearchBar from "../UI/SearchBar";
import Heading from "../UI/Heading";
import { BsPlusLg } from "react-icons/bs";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { requestPartnerAPI } from "../../api/server";
import PaginationMenu from "../UI/PaginationMenu";
import { MdAdd } from "react-icons/md";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import ListLabels from "../UI/ListLabels";
import PaymentsTour from "../tours/PaymentsTour";
import { UtilityContext } from "../../context/util-context";
import { formatCurrency } from "../../util/format-text";

export default function Payments() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default filter
  const defaultFilters = {
    query: "",
    limit: 50,
    status: "all",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(paramFilters);
  const [records, setRecords] = useState([]);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);
      // Fetch records based on filter
      const data = await requestPartnerAPI("fetch-payments", filter);
      setRecords(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Fetch on load or change of filter
  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="grid gap-4 overflow-hidden bg">
      <Heading
        title="Payments"
        description="This lists shows all the payments you have 
        requested or received from us. To send a new request, click 
        the '+' icon on the right. Yellow highlighted payments are pending and green highlighted payments are completed."
      >
        <div
          data-tour="payments-add"
          onClick={(e) => navigate("/payments/add", e)}
          className="flex items-center space-x-2 text-sm btn-secondary"
        >
          <p className="text-stone-200">Add Payment</p>
          <MdAdd className="text-xl text-stone-200" />
        </div>
      </Heading>
      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />
      {loading ? (
        <LoadingCard columns={4} max={6} />
      ) : records.length > 0 ? (
        <div className="grid gap-2">
          <div className="labels lg:grid-cols-7">
            <ListLabels
              labels={[
                "Date Paid",
                { name: "Description", classes: "lg:col-span-3" },
                "Vehicle",
                "Receipt",
                "Amount",
              ]}
              selected={filter.sort}
              setSelected={(sort) => setFilter({ ...filter, sort })}
            />
          </div>
          <div className="list">
            {records?.map((payment, idx) => (
              <PaymentDetail key={idx} payment={payment} />
            ))}
          </div>
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={filter.limit}
        setLimit={(l) => setFilter({ ...filter, limit: l })}
        loading={loading}
        count={records.length}
      >
        <div className="flex items-center space-x-2 text-sm">
          <p className="">Total:</p>
          <p className="font-semibold">
            {formatCurrency(records.reduce((acc, payment) => acc + payment.amount, 0))}
          </p>
        </div>
      </PaginationMenu>
    </div>
  );
}
